// Central date/time formatting for the admin. Stored timestamps are UTC; we render
// them in a single "active" timezone resolved (in priority order) from:
//   1. an explicit user override (persisted in localStorage), then
//   2. the workspace's country default, then
//   3. the browser's timezone.
//
// A module-level active timezone (set by useSyncTimeZone in the workspace layout)
// lets pure, non-hook formatters be called from anywhere — module helpers included —
// so every date across the site can format consistently without threading a hook
// through. Mirrors the env.ts pattern.

const TZ_OVERRIDE_KEY = "ph_timezone";

/** Primary IANA timezone for a workspace country (ISO-3166 alpha-2, lower-cased).
 *  Multi-timezone countries use their most common zone as the *default* — the user
 *  can override. Unknown countries fall back to the browser timezone. */
const COUNTRY_TZ: Record<string, string> = {
  gb: "Europe/London", ie: "Europe/Dublin", pt: "Europe/Lisbon", es: "Europe/Madrid",
  fr: "Europe/Paris", de: "Europe/Berlin", it: "Europe/Rome", nl: "Europe/Amsterdam",
  be: "Europe/Brussels", ch: "Europe/Zurich", at: "Europe/Vienna", se: "Europe/Stockholm",
  no: "Europe/Oslo", dk: "Europe/Copenhagen", fi: "Europe/Helsinki", pl: "Europe/Warsaw",
  gr: "Europe/Athens", cz: "Europe/Prague", ro: "Europe/Bucharest",
  us: "America/New_York", ca: "America/Toronto", mx: "America/Mexico_City",
  br: "America/Sao_Paulo", ar: "America/Argentina/Buenos_Aires", cl: "America/Santiago",
  au: "Australia/Sydney", nz: "Pacific/Auckland",
  ph: "Asia/Manila", sg: "Asia/Singapore", hk: "Asia/Hong_Kong", my: "Asia/Kuala_Lumpur",
  id: "Asia/Jakarta", th: "Asia/Bangkok", in: "Asia/Kolkata", jp: "Asia/Tokyo",
  kr: "Asia/Seoul", cn: "Asia/Shanghai", ae: "Asia/Dubai", sa: "Asia/Riyadh",
  za: "Africa/Johannesburg", ng: "Africa/Lagos", ke: "Africa/Nairobi", eg: "Africa/Cairo",
};

const browserTimeZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
};

/** The timezone for a workspace country, or undefined if we don't map it. */
export const countryTimeZone = (country?: string | null): string | undefined =>
  country ? COUNTRY_TZ[country.trim().toLowerCase()] : undefined;

/** Resolve the active timezone: explicit override → workspace country → browser. */
export const resolveTimeZone = (opts: { override?: string | null; country?: string | null }): string =>
  opts.override || countryTimeZone(opts.country) || browserTimeZone();

// --- persisted user override ------------------------------------------------

export const readTimeZoneOverride = (): string | null => {
  if (typeof localStorage === "undefined") return null;
  try {
    return localStorage.getItem(TZ_OVERRIDE_KEY);
  } catch {
    return null;
  }
};

export const writeTimeZoneOverride = (tz: string | null): void => {
  if (typeof localStorage === "undefined") return;
  try {
    if (tz) localStorage.setItem(TZ_OVERRIDE_KEY, tz);
    else localStorage.removeItem(TZ_OVERRIDE_KEY);
  } catch {
    /* ignore quota / privacy-mode */
  }
};

// --- module-level active timezone ------------------------------------------

let activeTimeZone: string = browserTimeZone();

export const setActiveTimeZone = (tz: string): void => {
  if (tz) activeTimeZone = tz;
};
export const getActiveTimeZone = (): string => activeTimeZone;

// --- value coercion + formatters -------------------------------------------

/** Coerce a stored value (epoch ms, epoch seconds, ISO string, numeric string) to a
 *  Date, or null if it isn't a real instant. */
export const toDate = (value: unknown): Date | null => {
  if (value === null || value === undefined || value === "") return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  let n: number | null = null;
  if (typeof value === "number") n = value;
  else if (typeof value === "string" && /^\d+$/.test(value)) n = Number(value);
  if (n !== null) {
    // Heuristic: 10-digit values are epoch SECONDS, 13-digit are milliseconds.
    if (n < 1e12) n *= 1000;
    const d = new Date(n);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(String(value)); // ISO / RFC string
  return Number.isNaN(d.getTime()) ? null : d;
};

const fmt = (
  value: unknown,
  options: Intl.DateTimeFormatOptions,
  tz?: string,
  fallback = "—",
): string => {
  const d = toDate(value);
  if (!d) return fallback;
  try {
    return new Intl.DateTimeFormat(undefined, { timeZone: tz || activeTimeZone, ...options }).format(d);
  } catch {
    return new Intl.DateTimeFormat(undefined, options).format(d);
  }
};

/** e.g. "25 Jul 2026" — a date only, in the active (or given) timezone. */
export const formatDate = (value: unknown, tz?: string): string =>
  fmt(value, { day: "2-digit", month: "short", year: "numeric" }, tz);

/** e.g. "25 Jul 2026, 14:32" — date + time, in the active (or given) timezone. */
export const formatDateTime = (value: unknown, tz?: string): string =>
  fmt(value, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }, tz);

/** e.g. "14:32" — time only. */
export const formatTime = (value: unknown, tz?: string): string =>
  fmt(value, { hour: "2-digit", minute: "2-digit" }, tz);

/** The local calendar day (YYYY-MM-DD) for a value IN the active timezone — used to
 *  bucket/compare UTC timestamps by the viewer's day (e.g. date-range filters, daily
 *  charts) without off-by-one errors from the browser zone. */
export const dayKeyInTimeZone = (value: unknown, tz?: string): string | null => {
  const d = toDate(value);
  if (!d) return null;
  try {
    // en-CA yields YYYY-MM-DD.
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: tz || activeTimeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(d);
  } catch {
    return d.toISOString().slice(0, 10);
  }
};

/** A curated timezone list for the override picker (grouped-ish, common zones first),
 *  always including the active + browser zones. */
export const timeZoneOptions = (): string[] => {
  const base = [
    "Europe/London", "Europe/Dublin", "Europe/Lisbon", "Europe/Madrid", "Europe/Paris",
    "Europe/Berlin", "Europe/Rome", "Europe/Amsterdam", "Europe/Athens",
    "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
    "America/Toronto", "America/Sao_Paulo", "America/Mexico_City",
    "Australia/Sydney", "Australia/Perth", "Pacific/Auckland",
    "Asia/Manila", "Asia/Singapore", "Asia/Hong_Kong", "Asia/Kolkata", "Asia/Tokyo",
    "Asia/Dubai", "Africa/Johannesburg", "UTC",
  ];
  const set = new Set<string>(base);
  set.add(browserTimeZone());
  set.add(activeTimeZone);
  return [...set];
};

/** A short label for a timezone incl. its current UTC offset, e.g. "Europe/London (GMT+1)". */
export const timeZoneLabel = (tz: string): string => {
  try {
    const parts = new Intl.DateTimeFormat("en-GB", { timeZone: tz, timeZoneName: "shortOffset" }).formatToParts(
      new Date(),
    );
    const off = parts.find((p) => p.type === "timeZoneName")?.value;
    return off ? `${tz.replace(/_/g, " ")} (${off})` : tz.replace(/_/g, " ");
  } catch {
    return tz.replace(/_/g, " ");
  }
};
